import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { fetchAPI } from "../../config/api";

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#6366f1",
];

const LoadingCard = () => (
  <div className="w-full max-w-2xl mx-auto mt-6">
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50">
        <div className="h-5 w-44 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-32 bg-gray-200 rounded mt-2 animate-pulse" />
      </div>
      <div className="p-6">
        <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    </div>
  </div>
);

const EmptyCard = ({ title, subtitle }) => (
  <div className="w-full max-w-2xl mx-auto mt-6">
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
      <div className="text-gray-900 font-semibold">{title}</div>
      <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
    </div>
  </div>
);

export const PositionChart = ({ playerId, year, division }) => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!playerId || !year) {
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          year: String(year),
          role: "batting",
        });
        if (division) params.append("division", String(division));

        const data = await fetchAPI(`/api/player/${playerId}/positions?${params.toString()}`);
        setSummary(Array.isArray(data.summary) ? data.summary : []);
      } catch (e) {
        setError(e?.message || "Failed to load position data");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [playerId, year, division]);

  const chartData = useMemo(() => {
    const batting = summary.filter((x) => x.role === "batting" && x.games > 0);
    const total = batting.reduce((s, x) => s + x.games, 0);
    if (!total) return [];

    return batting
      .map((x) => ({
        name: x.position || "UNK",
        value: x.games,
        pct: (x.games / total) * 100,
      }))
      .sort((a, b) => b.value - a.value);
  }, [summary]);

  const totals = useMemo(() => {
    const totalGames = chartData.reduce((s, x) => s + x.value, 0);
    const primary = chartData[0]?.name || null;
    const primaryPct = chartData[0]?.pct ?? null;
    return { totalGames, primary, primaryPct };
  }, [chartData]);

  const TooltipCard = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2">
        <div className="text-sm font-semibold text-gray-900">{d.name}</div>
        <div className="text-xs text-gray-600 mt-1">
          <span className="font-medium text-gray-900">{d.value}</span> games •{" "}
          <span className="font-medium text-gray-900">{d.pct.toFixed(1)}%</span>
        </div>
      </div>
    );
  };

  if (loading) return <LoadingCard />;

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto mt-6">
        <div className="rounded-xl border border-red-200 bg-red-50 shadow-sm p-4">
          <div className="text-sm font-semibold text-red-700">Couldn’t load positions</div>
          <div className="text-sm text-red-600 mt-1">{error}</div>
        </div>
      </div>
    );
  }

  if (!playerId || !year) {
    return <EmptyCard title="Positions" subtitle="Select a player and season to see defensive positions." />;
  }

  if (chartData.length === 0) {
    return <EmptyCard title="Positions" subtitle="No batting lineup position data found for this season." />;
  }

  return (
    <div className="w-full max-w-2xl mx-auto mt-6">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
          <div>
            <div className="text-base font-semibold text-gray-900">Games by Position</div>
            <div className="text-sm text-gray-600 mt-1">
              {totals.totalGames} game{totals.totalGames === 1 ? "" : "s"} • Primary:{" "}
              <span className="font-semibold text-gray-900">
                {totals.primary}
                {totals.primaryPct != null ? ` (${totals.primaryPct.toFixed(0)}%)` : ""}
              </span>
            </div>
          </div>
          <div className="text-xs text-gray-500 text-right">
            Season: <span className="font-medium text-gray-700">{year}</span>
            {division ? (
              <>
                <br />
                Division: <span className="font-medium text-gray-700">{division.replace("ncaa_", "")}</span>
              </>
            ) : null}
          </div>
        </div>

        <div className="p-6">
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="48%"
                innerRadius={70}
                outerRadius={110}
                paddingAngle={2}
                dataKey="value"
                labelLine={false}
              >
                {chartData.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>

              <Tooltip content={<TooltipCard />} />

              <Legend
                verticalAlign="bottom"
                height={44}
                formatter={(value, entry) => {
                  const d = entry?.payload;
                  if (!d) return value;
                  return (
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">{value}</span>{" "}
                      <span className="text-gray-500">— {d.value} ({d.pct.toFixed(0)}%)</span>
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
